import React, { useMemo, useState, useEffect, useRef } from 'react';
import BaseSelectBox from 'components/atoms/base-select-box';
import { readingTypesDataSource } from 'infrastructure/data-sources/reading-types';

import type {
  IBaseSelectBoxOption,
  IBaseSelectBoxProps,
} from 'components/atoms/base-select-box/types';
import type { IReadingTypeItem } from 'infrastructure/data-sources/reading-types';
import type { ReadingType } from 'infrastructure/enums';

type Options = IBaseSelectBoxOption<IReadingTypeItem['id'], IReadingTypeItem>[];

interface IReadingTypeSelectBoxProps
  extends Omit<IBaseSelectBoxProps<ReadingType>, 'options'> {
  withAll?: boolean;
  allOptionLabel?: string;
  closeOnScroll?: boolean;
}

const ReadingTypeSelectBox: React.FC<IReadingTypeSelectBoxProps> = (props) => {
  const { withAll, allOptionLabel, closeOnScroll = false } = props;
  const [open, setOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const dataSource = useMemo(
    () => readingTypesDataSource(withAll, allOptionLabel),
    [withAll, allOptionLabel],
  );

  const options: Options = useMemo(
    () =>
      dataSource.map((el) => ({
        value: el.id,
        label: el.name,
        record: el,
      })),
    [dataSource],
  );

  useEffect(() => {
    if (!closeOnScroll) return;

    const handleScroll = () => {
      if (open) {
        setOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, closeOnScroll]);

  return (
    <div ref={selectRef}>
      <BaseSelectBox
        options={options}
        {...props}
        open={closeOnScroll ? open : undefined}
        onDropdownVisibleChange={closeOnScroll ? setOpen : undefined}
      />
    </div>
  );
};

export default ReadingTypeSelectBox;
